import React, { useEffect, useState, useMemo } from 'react';
import Box from '@mui/material/Box';
import { useConfirm } from 'material-ui-confirm';
import { IconButton, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import KeyboardTabIcon from '@mui/icons-material/KeyboardTab';
import { format } from 'date-fns';
import HarvesterIcon from '../../../assets/icons/HarvesterIcon';
import TruckIcon from '../../../assets/icons/TruckIcon';
import BlockItem from './BlockItem';
import ItemBox from './ItemBox';
import NumberMenuBtn from './NumberMenuBtn';
import WarehouseItem from './WarehouseItem';
import {
  BLOCK_MIN_WIDTH,
  COMMON_PADDING,
  FIELD_MIN_WIDTH,
  PANEL_COLOR,
  ROUTE_BLOCK_HEIGHT,
  ROUTE_ICO_COLOR,
} from './constants';
import { useAppSelector } from '../../../app/store.js';
import reducerPath from '../../../app/reducerPath.js';
import FieldItem from './FieldItem';
import useCompanyTimeZone from '../../../entities/timeZone/hooks/useCompanyTimeZone';
import AdvicesInfoSection from '../../../features/logistics/AdvicesInfoSection';
import SolutionsInfoSection from './components/SolutionsInfoSection/SolutionsInfoSection';
import TopSection from './ui/PowerBalancingMainWidget/TopSection';
import {
  useChangeLogisticVehicleStateMutation,
  useDeleteHarvesterMutation,
  useDeleteVehicleMutation,
  usePatchLogisticTaskMutation,
} from '../../../entities/logistic/logisticTasks/redux/logisticTasks.api';
import AddVehiclesButton from './components/AddVehiclesButton/AddVehiclesButton';
import AbsoluteLoading from '../../../shared/ui/AbsoluteLoading/AbsoluteLoading';
import AddVehiclesPopup from '../../../features/vehicles/AddVehiclesPopup/AddVehiclesPopup';
import { logisticTasksBalanceResourceActions } from '../../../entities/logistic/logisticTasks/redux/logisticTasksBalance.slice';

const parseTime = (date, withTime = false) => {
  const year = new Intl.DateTimeFormat('en', { year: 'numeric' }).format(date);
  const month = new Intl.DateTimeFormat('en', { month: '2-digit' }).format(date);
  const day = new Intl.DateTimeFormat('en', { day: '2-digit' }).format(date);
  let time = '';
  if (withTime) {
    const h = `0${date.getHours()}`.slice(-2);
    const m = `0${date.getMinutes()}`.slice(-2);
    const s = `0${date.getSeconds()}`.slice(-2);
    time = `T${h}:${m}:${s}`;
  }
  return `${year}-${month}-${day}${time}`;
};

export default function PowerBalancingMainWidget(props) {
  const logisticTaskId = props?.logisticTask?.data?.logisticTaskId;
  const logisticTask = props?.logisticTask;
  const companyId = logisticTask?.data?.companyId;

  const [patchLogisticTask, { isLoading }] = usePatchLogisticTaskMutation();
  const [changeLogisticeVahicleState] = useChangeLogisticVehicleStateMutation();
  const [deleteHarvester] = useDeleteHarvesterMutation();
  const [deleteVehicle] = useDeleteVehicleMutation();

  const {
    data: { source, solution: remoteSolution },
    solution,
    stateData,
  } = useAppSelector((state) => state[`${reducerPath.logisticTasksBalanceResource}/counter`]);
  const addVehicleId = (items) => items.map((item) => ({ ...item, id: item.regNum }));
  const confirm = useConfirm();

  const [updateSolutionFlag, setUpdateSolutionFlag] = useState(true);

  const [typeAddVehiclesPopup, setTypeAddVehiclesPopup] = useState(null);

  const dispatch = useDispatch();
  useEffect(() => {
    if (!logisticTask?.data) return;
    dispatch(logisticTasksBalanceResourceActions.setData({ data: logisticTask?.data }));
  }, [logisticTask]);

  useEffect(() => {
    // Проверяем каждую минуту(?) актуальность временных интервалов решения
    const updateSolutionInterval = setInterval(() => {
      setUpdateSolutionFlag((oldVal) => !oldVal);
    }, 60000);
    return () => clearInterval(updateSolutionInterval);
  }, []);

  const companyTimeZone = useCompanyTimeZone();

  const todayWithCompanyTimeZone = useMemo(() => {
    const today = new Date();
    const todayLocalStr = parseTime(today, true);
    return new Date(`${todayLocalStr}${companyTimeZone || 'Z'}`);
  }, [companyTimeZone, updateSolutionFlag]);

  const solutionRoutes = useMemo(() => {
    if (!remoteSolution || !todayWithCompanyTimeZone) {
      return [];
    }
    const todayWithCompanyTimeZoneStr = parseTime(todayWithCompanyTimeZone);
    // MOCK:
    // const currentSolution = remoteSolution?.routesByDate?.['2025-05-23'];
    const currentSolution = remoteSolution?.routesByDate?.[todayWithCompanyTimeZoneStr]?.map((route) => {
      const currentRouteExportInfo = route.routeExportInfos?.[route.routeExportInfos.length - 1];

      return { ...route, fieldId: currentRouteExportInfo.fieldId, consumerId: currentRouteExportInfo.consumerId };
    });
    // TODO Привязка ТС к разным полям по времени из солюшена - отключена. TBD
    // if (currentSolution) {
    //   const todayMs = todayWithCompanyTimeZone.getTime();
    //   const res = currentSolution.filter((v) => {
    //     const startTime = v.startDate ? new Date(v.startDate) : 0;
    //     const finishTime = v.finishDate ? new Date(v.finishDate) : todayMs + 86400000;
    //     return todayMs >= startTime && todayMs <= finishTime;
    //   });
    //   return res;
    // }
    // return null;

    return currentSolution;
  }, [remoteSolution, todayWithCompanyTimeZone]);

  const { fields, warehouses, warehouseQueue, warehouseUnloading, harvesters, vehicles, vehiclesObj } = useMemo(() => {
    // Преобразуем массивы в объекты для быстрого поиска:
    const vehObj = source?.truckDetailsList?.reduce((acc, t) => ({ ...acc, [t.vehicleId]: t }), {});

    const { workVehicles, availableVehicles } =
      source?.truckDetailsList?.reduce(
        (acc, truck) => {
          if (solutionRoutes?.some(({ vehicleId }) => vehicleId === truck.vehicleId)) {
            acc.workVehicles.push(truck);
          } else {
            acc.availableVehicles.push(truck);
          }
          return acc;
        },
        {
          workVehicles: [],
          availableVehicles: [],
        },
      ) || {};

    const { workHarvesters, availableHarvesters } =
      source?.harvesterDetailsList?.reduce(
        (acc, hv) => {
          if (source?.fieldDetailsList?.some(({ geozoneId }) => hv.geozoneId === geozoneId)) {
            acc.workHarvesters.push(hv);
          } else {
            acc.availableHarvesters.push(hv);
          }
          return acc;
        },
        {
          workHarvesters: [],
          availableHarvesters: [],
        },
      ) || {};

    return {
      fields:
        source?.fieldDetailsList.map(({ geozoneId, fieldName, square, geozoneName, ...rest }) => {
          const fieldId = `${fieldName}_${geozoneName}`;
          return {
            geozoneId,
            fieldName,
            id: fieldId,
            bgColor: '#bbdf93',
            area: `${square} га`,
            square,
            geozoneName,
            harvesters: [],
            routes: solution.fields?.[fieldId]?.routes,
            percentHarvested: stateData?.stateMap?.fieldStates?.[geozoneId]?.percentHarvested,
            ...rest,
          };
        }) || [],
      warehouses:
        source?.warehouseDetailsList.map(({ name, geozoneUnload, productivity, volume, ...rest }) => ({
          name,
          id: `${name}_${geozoneUnload}`,
          geozoneUnload,
          vehicles: undefined,
          bgColor: '#ebe4a7',
          productivity,
          volume,
          ...rest,
        })) || [],
      harvesters: {
        total: addVehicleId(source?.harvesterDetailsList || []),
        available: addVehicleId(availableHarvesters || []),
        work: addVehicleId(workHarvesters || []),
      },
      vehicles: {
        total: addVehicleId(source?.truckDetailsList || []),
        available: addVehicleId(availableVehicles || []),
        work: addVehicleId(workVehicles || []),
      },
      warehouseQueue: undefined,
      warehouseUnloading: undefined,
      vehiclesObj: vehObj,
    };
  }, [source, solution, solutionRoutes, todayWithCompanyTimeZone, stateData]);

  const [showDetailed, setShowDetailed] = useState(false);

  const { currentSolution, fieldsWithSolutions, warehousesWithSolutions } = useMemo(() => {
    if (!solutionRoutes?.length) {
      return {
        currentSolution: solutionRoutes,
        fieldsWithSolutions: fields,
        warehousesWithSolutions: warehouses,
      };
    }
    const fieldsWSolutions = fields.map((f) => {
      const routesByGeozone = solutionRoutes
        ?.filter((sV) => sV.fieldId === f.geozoneId)
        .reduce((acc, r) => {
          const existingRoute = acc[r.consumerId] || { vehicleIds: [], vehicles: [] };
          if (!existingRoute.vehicleIds.includes(r.vehicleId)) {
            existingRoute.vehicleIds.push(r.vehicleId);
            existingRoute.vehicles.push(vehiclesObj[r.vehicleId]);
          }
          return { ...acc, [r.consumerId]: { ...r, ...existingRoute } };
        }, {});
      return {
        ...f,
        solutionRoutes: Object.values(routesByGeozone),
      };
    });
    const warehousesWSolutions = warehouses.map((w) => ({
      ...w,
      solutionRoutes: solutionRoutes?.filter((sV) => sV.consumerId === w.geozoneUnloadId),
    }));
    return {
      currentSolution: solutionRoutes,
      fieldsWithSolutions: fieldsWSolutions,
      warehousesWithSolutions: warehousesWSolutions,
    };
  }, [remoteSolution, fields, warehouses, solutionRoutes]);

  const mainPanelWidth = useMemo(
    () =>
      fieldsWithSolutions.reduce((acc, f) => {
        const w = Math.max(
          (f?.solutionRoutes ? Object.keys(f?.solutionRoutes).length || 1 : 1) * (BLOCK_MIN_WIDTH + COMMON_PADDING),
          FIELD_MIN_WIDTH,
        );
        return acc + w;
      }, 0),
    [fieldsWithSolutions, showDetailed],
  );

  const handleAddVehicle = (vehicle, fieldName) => {
    const newField = [
      ...source[fieldName],
      { vehicleId: vehicle.vehicleId, regNum: vehicle.num, model: vehicle.model.name },
    ];

    patchLogisticTask({ id: logisticTaskId, body: { logisticTaskId, source: { [fieldName]: newField } } });
  };

  const handleDeleteVehicle = (id, fieldName) => {
    const vehicle = source[fieldName].find((v) => v.vehicleId === id);
    const isLoadedVehicle = stateData?.stateMap?.vehicleStates?.[id]?.isLoaded;
    confirm({
      title: 'Удаление',
      confirmationText: 'Да',
      cancellationText: 'Отмена',
      description: (
        <Typography>
          Вы действительно хотите удалить транспорт «{vehicle.model} {vehicle.regNum}»?
          <br />
          {isLoadedVehicle ? <strong>Транспорт загружен.</strong> : ''}
        </Typography>
      ),
    })
      .then(() => {
        if (fieldName === 'truckDetailsList') deleteVehicle({ logisticTaskId, vehicleId: id });
        else deleteHarvester({ logisticTaskId, vehicleId: id });
      })
      .catch(() => {});
  };

  const handleChangeLogisticVehicleState = (vehicleId, state) => {
    changeLogisticeVahicleState({ logisticTaskId, vehicleId, state });
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: `${COMMON_PADDING}px`,
          minWidth: `${mainPanelWidth}px`,
        }}
        mb={`${COMMON_PADDING}px`}
      >
        <Box sx={{ width: `${(showDetailed ? 2.8 : 0.7) * BLOCK_MIN_WIDTH}px`, transition: 'width 0.3s ease-out' }}>
          <IconButton
            sx={{ margin: '0 3px' }}
            title={showDetailed ? 'Компактнее' : 'Подробнее'}
            onClick={() => setShowDetailed(!showDetailed)}
          >
            <KeyboardTabIcon
              sx={{
                transform: showDetailed ? 'scale(-1, 1)' : 'scale(1, 1)',
                color: (theme) => theme.palette.primary.main,
                transition: 'transform 0.3s',
              }}
            />
          </IconButton>
        </Box>
        <TopSection>
          <AdvicesInfoSection logisticTaskId={logisticTaskId} />
          <SolutionsInfoSection logisticTaskId={logisticTaskId} source={source} stateData={stateData} />
        </TopSection>
      </Box>
      <Box elevation={1} sx={{ display: 'flex' }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: `${COMMON_PADDING}px`,
            flexGrow: 1,
            maxWidth: '100%',
          }}
        >
          <Box sx={{ display: 'flex', maxWidth: '100%' }}>
            <Box>
              <BlockItem
                label="Производство"
                icon={<HarvesterIcon sx={{ width: '29px', height: '29px', color: ROUTE_ICO_COLOR }} />}
                detailed={showDetailed}
              >
                <AddVehiclesButton onClick={() => setTypeAddVehiclesPopup('harvesterDetailsList')} />
                <NumberMenuBtn
                  type="harvester"
                  color="#8e8e8e"
                  items={harvesters.total}
                  label="всего"
                  detailed={showDetailed}
                  captionLabel="Все комбайны"
                  deleteVehicle={(id) => handleDeleteVehicle(id, 'harvesterDetailsList')}
                  togglePauseVehicle={(id, state) => handleChangeLogisticVehicleState(id, state)}
                />
                <NumberMenuBtn
                  type="harvester"
                  color="text"
                  items={harvesters.work}
                  label="в работе"
                  detailed={showDetailed}
                  captionLabel="Комбайнов в работе"
                />
                <NumberMenuBtn
                  type="harvester"
                  color="error"
                  items={harvesters.maintenance}
                  label="в ремонте"
                  detailed={showDetailed}
                  captionLabel="Комбайны в ремонте"
                />
                <NumberMenuBtn
                  type="harvester"
                  color="#6aa02a"
                  items={harvesters.available}
                  label="доступно"
                  detailed={showDetailed}
                  captionLabel="Доступные комбайны"
                />
              </BlockItem>
              <BlockItem
                label="TC"
                sx={{ mt: `${COMMON_PADDING}px`, height: `${ROUTE_BLOCK_HEIGHT + 2 * COMMON_PADDING}px` }}
                detailed={showDetailed}
                icon={<TruckIcon sx={{ width: '30px', height: '30px', color: ROUTE_ICO_COLOR }} />}
              >
                <AddVehiclesButton onClick={() => setTypeAddVehiclesPopup('truckDetailsList')} />
                <NumberMenuBtn
                  type="vehicle"
                  color="#8e8e8e"
                  items={vehicles.total}
                  label="всего"
                  detailed={showDetailed}
                  captionLabel="Все TC"
                  deleteVehicle={(id) => handleDeleteVehicle(id, 'truckDetailsList')}
                  togglePauseVehicle={(id, state) => handleChangeLogisticVehicleState(id, state)}
                />
                <NumberMenuBtn
                  type="vehicle"
                  color="text"
                  items={vehicles.work}
                  label="в работе"
                  detailed={showDetailed}
                  captionLabel="ТС в работе"
                />
                <NumberMenuBtn
                  type="vehicle"
                  color="error"
                  items={vehicles.maintenance}
                  label="в ремонте"
                  detailed={showDetailed}
                  captionLabel="ТС в ремонте"
                />
                <NumberMenuBtn
                  type="vehicle"
                  color="#6aa02a"
                  items={vehicles.available}
                  label="доступно"
                  detailed={showDetailed}
                  captionLabel="Доступные ТС"
                />
              </BlockItem>
            </Box>
            {fieldsWithSolutions.length ? (
              fieldsWithSolutions.map(({ solutionRoutes: sRoutes, ...fld }, i) => {
                // const routeEntries = Object.entries(routes || {});
                const routesArr =
                  sRoutes?.map((route) => ({
                    id: route.consumerId,
                    warehouseId: route.consumerId,
                    warehouseIndex:
                      warehouses.findIndex(({ geozoneUnloadId }) => geozoneUnloadId === route.consumerId) || 0,
                    ...route,
                  })) || [];

                const harvestersCurrent = harvesters?.work?.filter((h) => h.geozoneId === fld.geozoneId) || [];

                const fieldVehicles =
                  currentSolution
                    ?.filter((cS) => cS?.fieldId === fld.geozoneId)
                    .map((v) => vehiclesObj?.[v.vehicleId]) || [];
                return (
                  <ItemBox key={fld.id} routes={routesArr}>
                    <FieldItem
                      latest={i === fields.length - 1}
                      availableHarvesters={harvesters.available}
                      warehouses={warehouses}
                      availableVehicles={vehicles.available}
                      {...fld}
                      routes={routesArr}
                      harvesters={harvestersCurrent}
                      vehicles={fieldVehicles}
                      cargoVolumeGraphics={stateData?.stateMap?.cargoVolumeGraphics}
                    />
                  </ItemBox>
                );
              })
            ) : (
              <ItemBox>
                <FieldItem id="_" latest />
              </ItemBox>
            )}
          </Box>
          <Box
            sx={{
              display: 'flex',
              backgroundColor: PANEL_COLOR,
              borderRadius: '8px',
              minWidth: `${mainPanelWidth + BLOCK_MIN_WIDTH * (showDetailed ? 2.8 : 0.7) + COMMON_PADDING}px`,
            }}
            wrap="nowrap"
          >
            <Box>
              <BlockItem
                label="Склады"
                sx={{ height: '100%' }}
                icon={<WarehouseIcon sx={{ width: '29px', height: '29px', color: ROUTE_ICO_COLOR }} />}
                detailed={showDetailed}
              >
                <NumberMenuBtn
                  type="vehicle"
                  color="text"
                  items={warehouseUnloading}
                  label="на разгрузке"
                  detailed={showDetailed}
                  captionLabel="ТС на разгрузке"
                />
                <NumberMenuBtn
                  type="vehicle"
                  color="error"
                  items={warehouseQueue}
                  label="в очереди"
                  detailed={showDetailed}
                  captionLabel="ТС в очереди"
                />
              </BlockItem>
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                maxWidth: `calc(100% - ${(showDetailed ? 2.8 : 0.7) * BLOCK_MIN_WIDTH + COMMON_PADDING}px)`,
                gap: `${COMMON_PADDING}px`,
                transition: 'max-width 0.3s ease-out',
                minWidth: `${mainPanelWidth}px`,
                width: '100%',
              }}
              py={`${COMMON_PADDING}px`}
              pr={`${COMMON_PADDING}px`}
              pl={`${COMMON_PADDING / 2}px`}
            >
              {warehousesWithSolutions.map((w) => (
                <WarehouseItem
                  key={w.id}
                  warehouse={w}
                  bgColor={w.bgColor}
                  chartData={stateData?.stateMap?.productVolume?.finishedProductsPoints}
                  planVolume={
                    stateData.stateMap?.transportTaskResult?.[format(todayWithCompanyTimeZone, 'yyyy-MM-dd')]
                      ?.avgExportOnConsumer?.[w.geozoneUnloadId]
                  }
                  vehicles={source?.truckDetailsList}
                />
              ))}
            </Box>
          </Box>
        </Box>
      </Box>
      <AddVehiclesPopup
        companyId={companyId}
        open={!!typeAddVehiclesPopup}
        onClose={() => setTypeAddVehiclesPopup(null)}
        onAdd={(vehicle) => handleAddVehicle(vehicle, typeAddVehiclesPopup)}
        existingVehicleIds={typeAddVehiclesPopup ? source[typeAddVehiclesPopup].map((v) => v.vehicleId) : []}
      />

      <AbsoluteLoading loading={isLoading} />
    </>
  );
}

PowerBalancingMainWidget.propTypes = {
  logisticTask: PropTypes.shape({
    data: PropTypes.shape({
      logisticTaskId: PropTypes.number.isRequired,
      companyId: PropTypes.number,
      status: PropTypes.string,
      type: PropTypes.string,
      createdDate: PropTypes.string,
      createdBy: PropTypes.string,
      lastModifiedDate: PropTypes.string,
      lastModifiedBy: PropTypes.string,
      name: PropTypes.string,
      isRunnable: PropTypes.bool,
      isAutostarted: PropTypes.bool,
      startTime: PropTypes.string,
      finishTime: PropTypes.string,
      source: PropTypes.shape({
        fieldDetailsList: PropTypes.arrayOf(
          PropTypes.shape({
            fieldName: PropTypes.string,
            geozoneName: PropTypes.string,
            cropType: PropTypes.string,
            square: PropTypes.number,
            cropPlanCapacity: PropTypes.number,
            cropDensity: PropTypes.number,
            harvestingStart: PropTypes.string,
            harvestingFinish: PropTypes.string,
            workdayStart: PropTypes.string,
            workdayFinish: PropTypes.string,
          }),
        ),
        harvesterDetailsList: PropTypes.arrayOf(
          PropTypes.shape({
            model: PropTypes.string,
            regNum: PropTypes.string,
            volume: PropTypes.number,
            productivity: PropTypes.number,
            availabilityStart: PropTypes.string,
            availabilityFinish: PropTypes.string,
            workdayStart: PropTypes.string,
            workdayFinish: PropTypes.string,
            geozoneName: PropTypes.string,
            cropTypes: PropTypes.arrayOf(PropTypes.string),
          }),
        ),
        truckDetailsList: PropTypes.arrayOf(
          PropTypes.shape({
            model: PropTypes.string,
            regNum: PropTypes.string,
            bodyVolume: PropTypes.number,
            trailerVolume: PropTypes.number,
            availabilityStart: PropTypes.string,
            availabilityFinish: PropTypes.string,
            workdayStart: PropTypes.string,
            workdayFinish: PropTypes.string,
            geozoneName: PropTypes.string,
            cropTypes: PropTypes.arrayOf(PropTypes.string),
            bodyType: PropTypes.string,
          }),
        ),
        warehouseDetailsList: PropTypes.arrayOf(
          PropTypes.shape({
            name: PropTypes.string,
            geozoneWait: PropTypes.string,
            geozoneUnload: PropTypes.string,
            queueLength: PropTypes.number,
            unloadSpeed: PropTypes.number,
            dayCropCapacity: PropTypes.number,
            hourCropCapacity: PropTypes.number,
            minUnloadTime: PropTypes.number,
            unloadStart: PropTypes.string,
            unloadFinish: PropTypes.string,
            workdayStart: PropTypes.string,
            workdayFinish: PropTypes.string,
            cropTypes: PropTypes.arrayOf(PropTypes.string),
            vehicleTypes: PropTypes.arrayOf(PropTypes.string),
          }),
        ),
        roadNetwork: PropTypes.shape({
          fieldNames: PropTypes.arrayOf(PropTypes.string),
          warehouseNames: PropTypes.arrayOf(PropTypes.string),
          distanceMatrix: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.number)),
        }),
        balanceCapacityParameters: PropTypes.shape({
          minVolume: PropTypes.number,
          maxVolume: PropTypes.number,
          fieldSnapRadius: PropTypes.number,
          harvesterSnapRadius: PropTypes.number,
          storageSnapRadius: PropTypes.number,
          priorityType: PropTypes.string,
        }),
      }),
      // TODO TBD
      // eslint-disable-next-line react/forbid-prop-types
      solution: PropTypes.any,
    }),
    isLoading: PropTypes.bool,
    isError: PropTypes.bool,
  }),
  patchLogisticTask: PropTypes.func,
  patchLogisticTaskSolve: PropTypes.func,
  solveLogisticTask: PropTypes.shape({
    isLoading: PropTypes.bool,
    isError: PropTypes.bool,
  }),
  resultPatchLogisticTask: PropTypes.shape({
    isLoading: PropTypes.bool,
    isError: PropTypes.bool,
  }),
};

PowerBalancingMainWidget.defaultProps = {
  logisticTask: null,
  solveLogisticTask: null,
  resultPatchLogisticTask: null,
  patchLogisticTask: () => {},
  patchLogisticTaskSolve: () => {},
};
